import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { closeIssues, getIssues, Issue } from '../../api/issues';
import { follow, getFollows, getTimeline, previewRequeue, requeueIssues, RequeuePreview, Timeline } from '../data/cockpit';
import { envKind, useCockpit } from '../data/CockpitContext';
import { ago, when } from '../data/format';
import { apiError, usePoll } from '../data/usePoll';
import { CheckRow, Empty, EnvChip, Kv, OwnerChip, Spinner } from '../components/ui';
import { DOC_COLORS, FlowItem, FlowTimeline } from '../components/FlowTimeline';
import { v2 } from '../paths';
import { issueLabel, objectName, ownerOfIssue, verdictLook } from './issueModel';
import { IssuesPage } from './IssuesPage';
import { timelineFlow } from './timelineFlow';

/** Without /Cockpit/timeline: what the issue itself knows, as a short flow. */
const fallbackFlow = (i: Issue) => {
    const doc = { id: 'doc', label: `${objectName(i.objectType)} ${issueLabel(i)}`, color: DOC_COLORS[0] };
    const items: FlowItem[] = [
        { phase: doc.label },
        { at: i.firstFailedAt, documentId: 'doc', lane: 'cb', look: 'ok', title: 'In Chargebee', detail: i.eventType ?? '' },
        { at: i.firstFailedAt, documentId: 'doc', lane: 'cn', look: 'ok', title: 'Ontvangen', detail: '', app: 'cbrcv' },
        {
            at: i.lastFailedAt,
            documentId: 'doc',
            lane: i.exactState === 'InExact' ? 'ex' : 'cn',
            look: 'bad',
            title: i.title,
            detail: `${i.attempts} pogingen`,
            app: i.exactState === 'InExact' ? 'exsnd' : undefined,
        },
        { now: when(new Date()) },
    ];
    return { items, documents: [doc] };
};

export const IssuePanel = () => {
    const { customer, environment, key } = useParams();
    const axios = useAxiosSwagger();
    const navigate = useNavigate();
    const { find } = useCockpit();
    const env = find(customer, environment);
    const [confirm, setConfirm] = useState(false);
    const [closing, setClosing] = useState(false);
    const [reason, setReason] = useState('');
    const [result, setResult] = useState<{ text: string; tone: 'ok' | 'bad' | 'info' }>();
    const [followed, setFollowed] = useState(false);

    const relation = (key ?? '').split('|').pop() ?? '';
    const issue = usePoll<Issue | undefined>(
        axios && customer && environment && key
            ? async (s) => (await getIssues(axios, { customer, environment, q: relation, status: 'all' }, s)).items.find((i) => i.key === key)
            : null,
        15000,
        [axios, customer, environment, key]
    );
    const timeline = usePoll<Timeline>(axios && customer && environment && key ? (s) => getTimeline(axios, customer, environment, { key }, s) : null, 15000, [axios, customer, environment, key]);
    const preview = usePoll<RequeuePreview | undefined>(
        axios && customer && environment && key ? async (s) => (await previewRequeue(axios, [{ customer, environment, key }], s))[0] : null,
        60000,
        [axios, customer, environment, key]
    );
    const follows = usePoll(axios ? (s: AbortSignal) => getFollows(axios, s) : null, 30000, [axios]);

    const i = issue.data;
    const flow = useMemo(() => (timeline.data && timeline.data.events.length > 0 ? timelineFlow(timeline.data) : i ? fallbackFlow(i) : undefined), [timeline.data, i]);

    if (!customer || !environment || !key) return null;

    const close = () => navigate(v2.envIssues(customer, environment));
    const owner = i ? ownerOfIssue(i) : 'us';
    const kind = env ? envKind(env) : environment.toLowerCase().startsWith('prod') ? 'prod' : 'test';
    const isFollowed = followed || (follows.data ?? []).some((f) => f.customer === customer && f.environment === environment && f.relationId === i?.relationId);
    const look = verdictLook(preview.data);
    const canOffer = preview.data?.verdict === 'may';
    const resolved = i && (i.status === 'Resolved' || i.status === 'Closed');

    const doFollow = async () => {
        if (!axios || !i) return;
        try {
            await follow(axios, { customer, environment, objectType: i.objectType, relationId: i.relationId, label: issueLabel(i) });
            setFollowed(true);
        } catch {
            setResult({ text: 'Volgen lukte niet', tone: 'bad' });
        }
    };

    const doOffer = async () => {
        if (!axios) return;
        setConfirm(false);
        setResult({ text: 'Aanbieden…', tone: 'info' });
        try {
            const [r] = await requeueIssues(axios, [{ customer, environment, key }]);
            setResult(r && r.state !== 'Refused' && r.state !== 'Failed' ? { text: `Aangeboden${r.state === 'DryRun' ? ' (dry run)' : ''}: we volgen het live`, tone: 'info' } : { text: `Tegengehouden: ${r?.note ?? 'onbekend'}`, tone: 'bad' });
            if (!isFollowed) doFollow();
            issue.reload();
            timeline.reload();
        } catch (e: any) {
            setResult({ text: apiError(e), tone: 'bad' });
        }
    };

    const doClose = async () => {
        if (!axios || !reason.trim()) return;
        try {
            await closeIssues(axios, [{ customer, environment, key }], reason.trim());
            setClosing(false);
            setResult({ text: 'Gesloten', tone: 'ok' });
            issue.reload();
        } catch {
            setResult({ text: 'Sluiten lukte niet', tone: 'bad' });
        }
    };

    const nextTitle = resolved
        ? 'Opgelost'
        : owner === 'cu'
          ? 'De klant moet iets doen'
          : owner === 'au'
            ? 'Lost zichzelf op'
            : canOffer
              ? 'Bied het opnieuw aan'
              : i?.interpretation?.action ?? 'Uitzoeken';

    return (
        <>
            <IssuesPage />
            <button className="scrim" aria-label="Paneel sluiten" onClick={close} />
            <aside className="drawer" aria-label="Issue">
                <div className="dhead">
                    <div className="hrow">
                        <EnvChip kind={kind} environment={environment} />
                        <span className="mut">{customer}</span>
                        <span className="mut">/</span>
                        <span className="mono">{i ? issueLabel(i) : relation}</span>
                        <span className="grow" />
                        {i && (
                            <Link
                                className="btn sm ghost"
                                to={v2.object(i.customer, i.environment, i.invoiceId || i.relationId)}
                                state={{ from: window.location.pathname + window.location.search, label: `issue ${issueLabel(i)}` }}
                            >
                                Dossier
                            </Link>
                        )}
                        <button className={isFollowed ? 'btn sm' : 'btn sm ghost'} onClick={doFollow} disabled={isFollowed}>
                            {isFollowed ? 'Gevolgd' : 'Volgen'}
                        </button>
                        <button className="btn sm ghost" onClick={() => navigator.clipboard?.writeText(window.location.href)}>
                            Link
                        </button>
                        <button className="btn sm ghost" onClick={close}>
                            Esc
                        </button>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.25 }}>{i?.title ?? (issue.loading ? 'Laden…' : 'Issue niet gevonden')}</div>
                    {i && (
                        <div className="hrow">
                            <OwnerChip owner={owner} long />
                            {i.unrecognised && <span className="chip warn">Nog geen regel</span>}
                            <span className="chip">Open {ago(i.firstFailedAt)}</span>
                            <span className="chip">{i.attempts} pogingen</span>
                            {i.reviewNote && <span className="chip info" title={i.reviewNote}>Herbeoordeeld</span>}
                        </div>
                    )}
                </div>

                {issue.error && <div className="dsec"><div className="notice">{issue.error}</div></div>}

                {i && (
                    <div className="dsec">
                        <div className={`next ${resolved ? 'au' : owner}`}>
                            <span className={`who ${resolved ? 'au' : owner}`}>{resolved ? '✓' : owner === 'us' ? 'WIJ' : owner === 'cu' ? 'KLANT' : '↻'}</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ fontSize: 18, fontWeight: 600 }}>{nextTitle}</div>
                                {i.interpretation?.explanation && <div className="mut">{i.interpretation.explanation}</div>}
                                {(i.interpretation?.steps?.length ?? 0) > 0 && (
                                    <ol className="steps-num">
                                        {i.interpretation!.steps!.slice(0, 4).map((s, n) => (
                                            <li key={n}><span>{s}</span></li>
                                        ))}
                                    </ol>
                                )}
                                {!resolved && (
                                    <div className="hrow">
                                        {canOffer && <button className="btn pri" onClick={() => setConfirm(true)}>Opnieuw aanbieden</button>}
                                        {owner === 'cu' && <Link className="btn" to={v2.learn('mails')}>Klantmail</Link>}
                                        <button className="btn ghost" onClick={() => setClosing(!closing)}>Sluiten met reden</button>
                                    </div>
                                )}
                                {closing && (
                                    <div className="hrow">
                                        <label className="search" style={{ flex: 1 }}>
                                            <input aria-label="Reden" placeholder="Waarom sluiten?" value={reason} onChange={(e) => setReason(e.target.value)} />
                                        </label>
                                        <button className="btn" disabled={!reason.trim()} onClick={doClose}>Sluiten</button>
                                    </div>
                                )}
                                {result && (
                                    <div className={`hrow ${result.tone}`} style={{ gap: 6 }}>
                                        {result.tone === 'info' && <Spinner />}
                                        {result.text}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="dsec">
                    <div className="hrow" style={{ marginBottom: 10, justifyContent: 'space-between' }}>
                        <span className="ct">Wat er gebeurde</span>
                        {timeline.error && <span className="mut" style={{ fontSize: 12 }}>uit het issue zelf</span>}
                    </div>
                    {flow ? <FlowTimeline items={flow.items} documents={flow.documents} /> : <Empty>Laden…</Empty>}
                </div>

                {i && (
                    <div className="dsec g2e">
                        <div>
                            <div className="ct" style={{ marginBottom: 6 }}>Zelf nagekeken</div>
                            <div className="checks">
                                {i.checks.length === 0 && <span className="mut" style={{ fontSize: 13 }}>{i.checkable ? 'Nog niet nagekeken' : 'Geen controle voor deze fout'}</span>}
                                {i.checks.map((c, n) => (
                                    <CheckRow key={n} tone={c.ok ? 'ok' : 'bad'} label={c.text} />
                                ))}
                                <CheckRow tone={canOffer ? 'ok' : preview.data?.verdict === 'no' ? 'bad' : 'wait'} label="Opnieuw aanbieden" value={look.text} />
                            </div>
                            {i.checksAt && <div className="mut" style={{ fontSize: 12, marginTop: 6 }}>nagekeken {ago(i.checksAt)} geleden{i.checkedInExact ? ' · in Exact' : ''}</div>}
                        </div>
                        <div>
                            <div className="ct" style={{ marginBottom: 6 }}>Feiten</div>
                            <Kv
                                rows={[
                                    ['Soort', objectName(i.objectType)],
                                    ['Id', <span className="mono">{i.relationId}</span>],
                                    ['Factuur', i.invoiceId ? <span className="mono">{i.invoiceId}</span> : '–'],
                                    ['Klant', i.chargebeeCustomerId ? <span className="mono">{i.chargebeeCustomerId}</span> : '–'],
                                    ['In Exact', i.exactState === 'InExact' ? 'ja' : i.exactState === 'NotInExact' ? 'nee' : i.exactState],
                                    ['Eerste fout', when(i.firstFailedAt)],
                                    ['Laatste fout', when(i.lastFailedAt)],
                                    ['Probleemcode', <span className="mono">{i.code}</span>],
                                ]}
                            />
                        </div>
                    </div>
                )}

                {i?.errorMessage && (
                    <div className="dsec">
                        <details>
                            <summary className="ct" style={{ cursor: 'pointer' }}>Melding van de connector</summary>
                            <div className="codebox" style={{ marginTop: 8 }}>{i.errorMessage}</div>
                        </details>
                    </div>
                )}

                {i && (
                    <div className="dsec">
                        <div className="ct" style={{ marginBottom: 6 }}>Activiteit</div>
                        <div className="checks">
                            {i.closedAt && <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.closedAt)}</span><span className="own au">{i.closedBy ?? 'iemand'}</span><span>Gesloten: {i.closedReason}</span></div>}
                            {i.reviewedAt && <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.reviewedAt)}</span><span className="own au">{i.reviewedBy ?? 'Herbeoordeling'}</span><span>{i.reviewNote}</span></div>}
                            {i.autoRequeueNote && <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.nextAutoRequeueAt ?? i.lastCheckedAt)}</span><span className="own au">{i.autoRequeueBy ?? 'Scanner'}</span><span>{i.autoRequeueNote}</span></div>}
                            {i.lastCheckedAt && <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.lastCheckedAt)}</span><span className="own au">Scanner</span><span>Nagekeken</span></div>}
                            <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.firstFailedAt)}</span><span className="own au">Connector</span><span>Eerste mislukte poging</span></div>
                        </div>
                    </div>
                )}
            </aside>

            {confirm && i && (
                <>
                    <button className="scrim dialog-scrim" aria-label="Annuleren" onClick={() => setConfirm(false)} />
                    <div className="dialog" role="dialog" aria-label="Opnieuw aanbieden">
                        <div className="ch">
                            <span className="h2">{objectName(i.objectType)} {issueLabel(i)} opnieuw aanbieden</span>
                            <EnvChip kind={kind} environment={`${customer} ${environment}`} />
                        </div>
                        <div className="cb g2e">
                            <div>
                                <div className="eyebrow" style={{ marginBottom: 6 }}>Gaat opnieuw</div>
                                <div className="checks">
                                    <CheckRow tone="wait" mark="1" label={`${objectName(i.objectType)} naar de connector`} value="queue" />
                                    <CheckRow tone="wait" mark="2" label="Daarna naar Exact" value="exsnd" />
                                </div>
                            </div>
                            <div>
                                <div className="eyebrow" style={{ marginBottom: 6 }}>Vooraf gecontroleerd</div>
                                <div className="checks">
                                    <CheckRow tone="ok" label="Controles" value={look.text} />
                                    {i.checks.slice(0, 3).map((c, n) => (
                                        <CheckRow key={n} tone={c.ok ? 'ok' : 'bad'} label={c.text} />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="ch" style={{ borderTop: '1px solid #1A212B', borderBottom: 0, justifyContent: 'flex-end' }}>
                            <button className="btn ghost" onClick={() => setConfirm(false)}>Annuleren</button>
                            <button className="btn pri" onClick={doOffer}>Aanbieden en volgen</button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
